/**
 * Admin Clubs Management - CRUD de clubes y generación de QR codes
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Plus, Search, Download, QrCode, MapPin, Phone, Mail,
  ChevronLeft, Pencil, Trash2, X, Check, RefreshCw, Globe, Save, Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// Add Club Modal
const AddClubModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    state: '',
    country: 'México',
    address: '',
    phone: '',
    email: '',
    lat: '',
    lng: '',
    num_courts: 2
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.name || !formData.city) {
      toast.error('Nombre y ciudad son obligatorios');
      return;
    }

    try {
      setSaving(true);
      
      const clubData = {
        name: formData.name,
        city: formData.city,
        state: formData.state || formData.city,
        country: formData.country,
        address: formData.address,
        phone: formData.phone,
        email: formData.email,
        location: {
          lat: formData.lat ? parseFloat(formData.lat) : 19.4326,
          lng: formData.lng ? parseFloat(formData.lng) : -99.1332
        },
        courts: Array.from({ length: formData.num_courts }, (_, i) => ({
          id: `court-new-${Date.now()}-${i + 1}`,
          court_number: i + 1
        }))
      };

      await adminAPI.createClub(clubData);
      toast.success('Club creado exitosamente');
      onSave();
      onClose();
      
      // Reset form
      setFormData({
        name: '',
        city: '',
        state: '',
        country: 'México',
        address: '',
        phone: '',
        email: '',
        lat: '',
        lng: '',
        num_courts: 2
      });
    } catch (err) {
      console.error('Create error:', err);
      toast.error(err.response?.data?.detail || 'Error creando club');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-lg max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            Agregar Nuevo Club
          </DialogTitle>
          <DialogDescription>
            Completa los datos del nuevo club. Los campos con * son obligatorios.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="add-name">Nombre del Club *</Label>
            <Input
              id="add-name"
              data-testid="admin-add-club-name-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre del club"
            />
          </div>
          
          {/* City & State */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="add-city">Ciudad *</Label>
              <Input
                id="add-city"
                data-testid="admin-add-club-city-input"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Ciudad"
              />
            </div>
            <div>
              <Label htmlFor="add-state">Estado</Label>
              <Input
                id="add-state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="Estado/Provincia"
              />
            </div>
          </div>
          
          {/* Country */}
          <div>
            <Label htmlFor="add-country">País</Label>
            <Select 
              value={formData.country} 
              onValueChange={(val) => setFormData({ ...formData, country: val })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="México">🇲🇽 México</SelectItem>
                <SelectItem value="USA">🇺🇸 USA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Address */}
          <div>
            <Label htmlFor="add-address">Dirección</Label>
            <Input
              id="add-address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Dirección completa"
            />
          </div>
          
          {/* Phone & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="add-phone">Teléfono</Label>
              <Input
                id="add-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+52 XXX XXX XXXX"
              />
            </div>
            <div>
              <Label htmlFor="add-email">Email</Label>
              <Input
                id="add-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@club.com"
              />
            </div>
          </div>
          
          {/* Coordinates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="add-lat">Latitud</Label>
              <Input
                id="add-lat"
                type="number"
                step="any"
                value={formData.lat}
                onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                placeholder="ej: 19.4326"
              />
            </div>
            <div>
              <Label htmlFor="add-lng">Longitud</Label>
              <Input
                id="add-lng"
                type="number"
                step="any"
                value={formData.lng}
                onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                placeholder="ej: -99.1332"
              />
            </div>
          </div>
          
          {/* Number of courts */}
          <div>
            <Label htmlFor="add-courts">Número de Canchas</Label>
            <Select 
              value={String(formData.num_courts)} 
              onValueChange={(val) => setFormData({ ...formData, num_courts: parseInt(val) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                  <SelectItem key={n} value={String(n)}>{n} cancha{n > 1 ? 's' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="admin-add-club-submit-button">
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Crear Club
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Manage Courts Modal
const ManageCourtsModal = ({ isOpen, onClose, club, onSave }) => {
  const [courts, setCourts] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingCourtPdfId, setDownloadingCourtPdfId] = useState(null);

  const [assigningId, setAssigningId] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCourtNumber, setNewCourtNumber] = useState('');
  const [editingCourtId, setEditingCourtId] = useState(null);
  const [editCourtNumber, setEditCourtNumber] = useState('');

  useEffect(() => {
    if (club) {
      setCourts(club.courts || []);
      fetchAvailableCameras();
    }
  }, [club]);

  const fetchAvailableCameras = async () => {
    try {
      const response = await adminAPI.getAvailableCameras();
      setAvailableCameras(response.data.cameras || []);
    } catch (err) {
      console.error('Error fetching cameras:', err);
    }
  };

  const handleAddCourt = async (courtNumber = null) => {
    if (!club) return;
    
    try {
      setAdding(true);
      const response = await adminAPI.addCourt(club.id, courtNumber);
      const newCourt = response.data.court;
      setCourts([...courts, newCourt].sort((a, b) => a.court_number - b.court_number));
      toast.success(`Cancha ${newCourt.court_number} agregada`);
      setShowAddDialog(false);
      setNewCourtNumber('');
      onSave(); // Refresh parent
    } catch (err) {
      console.error('Add court error:', err);
      toast.error(err.response?.data?.detail || 'Error agregando cancha');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateCourtNumber = async (courtId) => {
    if (!club || !editCourtNumber) return;
    
    const numValue = parseInt(editCourtNumber);
    if (isNaN(numValue) || numValue < 1) {
      toast.error('Número de cancha inválido');
      return;
    }
    
    try {
      await adminAPI.updateCourt(club.id, courtId, numValue);
      setCourts(courts.map(c => 
        c.id === courtId ? { ...c, court_number: numValue } : c
      ).sort((a, b) => a.court_number - b.court_number));
      toast.success(`Cancha renumerada a ${numValue}`);
      setEditingCourtId(null);
      setEditCourtNumber('');
      onSave();
    } catch (err) {
      console.error('Update court error:', err);
      toast.error(err.response?.data?.detail || 'Error actualizando cancha');
    }
  };

  const handleDeleteCourt = async (courtId, courtNumber) => {
    if (!club) return;
    
    // Confirm deletion
    if (!window.confirm(`¿Estás seguro de eliminar la Cancha ${courtNumber}? Esta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      setDeletingId(courtId);
      await adminAPI.deleteCourt(club.id, courtId);
      setCourts(courts.filter(c => c.id !== courtId));
      toast.success(`Cancha ${courtNumber} eliminada`);
      onSave(); // Refresh parent
    } catch (err) {
      console.error('Delete court error:', err);
      toast.error(err.response?.data?.detail || 'Error eliminando cancha');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAssignCamera = async (courtId, cameraId) => {
    if (!club || !cameraId) return;
    
    try {
      setAssigningId(courtId);
      await adminAPI.assignCameraToCourtAPI(club.id, courtId, cameraId);
      
      // Update local state with new format (array)
      setCourts(courts.map(c => 
        c.id === courtId ? { ...c, hikconnect_camera_ids: [cameraId] } : c
      ));
      
      // Refresh available cameras
      await fetchAvailableCameras();
      
      const camera = availableCameras.find(cam => cam.id === cameraId);
      toast.success(`Cámara "${camera?.name || cameraId}" asignada`);
      onSave();
    } catch (err) {
      console.error('Assign camera error:', err);
      toast.error(err.response?.data?.detail || 'Error asignando cámara');
    } finally {
      setAssigningId(null);
    }
  };

  const handleUnassignCamera = async (courtId) => {
    if (!club) return;
    
    try {
      setAssigningId(courtId);
      await adminAPI.unassignCameraFromCourt(club.id, courtId);
      
      // Update local state
      setCourts(courts.map(c => 
        c.id === courtId ? { ...c, hikconnect_camera_ids: [] } : c
      ));
      
      // Refresh available cameras
      await fetchAvailableCameras();
      
      toast.success('Cámara desasignada');
      onSave();
    } catch (err) {
      console.error('Unassign camera error:', err);
      toast.error(err.response?.data?.detail || 'Error desasignando cámara');
    } finally {
      setAssigningId(null);
    }
  };

  const handleDownloadCourtPDF = async (court) => {
    if (!club || !court) return;

    try {
      setDownloadingCourtPdfId(court.id);
      const response = await adminAPI.downloadCourtPDF(club.id, court.id);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${club.name.replace(/\s+/g, '_')}_cancha_${court.court_number}_pdf_cancha.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('PDF cancha descargado');
    } catch (err) {
      console.error('Download court PDF error:', err);
      toast.error(err.response?.data?.detail || 'No se pudo descargar el PDF cancha');
    } finally {
      setDownloadingCourtPdfId(null);
    }
  };

  // Helper to get camera IDs (supports both old and new field)
  const getCourtCameraIds = (court) => {
    if (court.hikconnect_camera_ids && court.hikconnect_camera_ids.length > 0) {
      return court.hikconnect_camera_ids;
    }
    if (court.hikconnect_camera_id) {
      return [court.hikconnect_camera_id];
    }
    return [];
  };

  const getCameraName = (cameraId) => {
    const camera = availableCameras.find(c => c.id === cameraId);
    return camera?.name || `Cámara ${cameraId?.slice(0, 8)}...`;
  };

  if (!club) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-lg max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            Gestión de Canchas y Cámaras
          </DialogTitle>
          <DialogDescription>
            {club.name} - Asigna cámaras de Hik-Connect a cada cancha
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Info banner */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-sm">
            <p className="text-blue-400 font-medium">⚠️ Importante</p>
            <p className="text-muted-foreground text-xs mt-1">
              Cada cancha debe tener <strong>al menos una cámara</strong> asignada para que los replays funcionen correctamente.
            </p>
            <p className="text-muted-foreground text-xs mt-1 opacity-70">
              💡 En el futuro se podrán asignar múltiples cámaras por cancha.
            </p>
          </div>
          
          {/* Courts List */}
          <div className="border rounded-lg divide-y max-h-[36dvh] sm:max-h-[400px] overflow-y-auto overscroll-contain">
            {courts.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No hay canchas registradas
              </div>
            ) : (
              courts.map((court) => {
                const cameraIds = getCourtCameraIds(court);
                const hasCamera = cameraIds.length > 0;
                const isEditing = editingCourtId === court.id;
                
                return (
                  <div key={court.id} className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={editCourtNumber}
                              onChange={(e) => setEditCourtNumber(e.target.value)}
                              className="w-16 h-10 text-center"
                              autoFocus
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-green-400 hover:text-green-300"
                              onClick={() => handleUpdateCourtNumber(court.id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground"
                              onClick={() => {
                                setEditingCourtId(null);
                                setEditCourtNumber('');
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div 
                              className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center cursor-pointer hover:bg-primary/20 transition-colors"
                              onClick={() => {
                                setEditingCourtId(court.id);
                                setEditCourtNumber(String(court.court_number));
                              }}
                              title="Clic para editar número"
                            >
                              <span className="font-bold text-primary">{court.court_number}</span>
                            </div>
                            <div>
                              <p className="font-medium">Cancha {court.court_number}</p>
                              <p className="text-xs text-muted-foreground">
                                {hasCamera ? (
                                  <span className="text-green-400">✓ {cameraIds.length} cámara(s) asignada(s)</span>
                                ) : (
                                  <span className="text-yellow-400">⚠ Sin cámara - Requerida</span>
                                )}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        {!isEditing && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setEditingCourtId(court.id);
                              setEditCourtNumber(String(court.court_number));
                            }}
                            title="Editar número de cancha"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteCourt(court.id, court.court_number)}
                          disabled={deletingId === court.id || courts.length <= 1}
                          title={courts.length <= 1 ? "No puedes eliminar la última cancha" : "Eliminar cancha"}
                        >
                          {deletingId === court.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Camera assignment */}
                    <div className="flex items-center gap-2 pl-13">
                      <Select
                        value={cameraIds[0] || ""}
                        onValueChange={(value) => {
                          if (value === "__unassign__") {
                            handleUnassignCamera(court.id);
                          } else if (value) {
                            handleAssignCamera(court.id, value);
                          }
                        }}
                        disabled={assigningId === court.id}
                      >
                        <SelectTrigger className="flex-1 h-9 text-xs">
                          <SelectValue placeholder="⚠️ Seleccionar cámara (requerido)..." />
                        </SelectTrigger>
                        <SelectContent>
                          {hasCamera && (
                            <SelectItem value="__unassign__" className="text-destructive">
                              ✕ Quitar asignación
                            </SelectItem>
                          )}
                          {availableCameras.map((camera) => (
                            <SelectItem 
                              key={camera.id} 
                              value={camera.id}
                              disabled={camera.is_assigned && !cameraIds.includes(camera.id)}
                            >
                              <span className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${camera.online ? 'bg-green-400' : 'bg-red-400'}`} />
                                {camera.name}
                                {camera.is_assigned && !cameraIds.includes(camera.id) && (
                                  <span className="text-muted-foreground">(asignada a otra cancha)</span>
                                )}
                              </span>
                            </SelectItem>
                          ))}
                          {availableCameras.length === 0 && (
                            <SelectItem value="__none__" disabled>
                              No hay cámaras disponibles en Hik-Connect
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      
                      {/* Botón para quitar cámara - más visible */}
                      {hasCamera && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 shrink-0"
                          onClick={() => handleUnassignCamera(court.id)}
                          disabled={assigningId === court.id}
                          title="Quitar cámara asignada"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {assigningId === court.id && (
                        <RefreshCw className="w-4 h-4 animate-spin text-primary" />
                      )}
                    </div>
                    
                    {/* PDF cancha download button */}
                    <div className="pl-13 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-xs"
                        onClick={() => handleDownloadCourtPDF(court)}
                        disabled={downloadingCourtPdfId === court.id}
                        data-testid={`admin-court-pdf-cancha-button-${court.id}`}
                      >
                        {downloadingCourtPdfId === court.id ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4 mr-2" />
                        )}
                        PDF cancha
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Add Court Section */}
          {showAddDialog ? (
            <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
              <p className="text-sm font-medium">Agregar nueva cancha</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  placeholder="Número de cancha"
                  value={newCourtNumber}
                  onChange={(e) => setNewCourtNumber(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={() => handleAddCourt(newCourtNumber ? parseInt(newCourtNumber) : null)} 
                  disabled={adding}
                  size="sm"
                >
                  {adding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setShowAddDialog(false);
                    setNewCourtNumber('');
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Deja vacío para auto-asignar el siguiente número disponible
              </p>
            </div>
          ) : (
            <Button 
              onClick={() => setShowAddDialog(true)} 
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Nueva Cancha
            </Button>
          )}
          
          {/* Info */}
          <p className="text-xs text-muted-foreground text-center">
            Total: {courts.length} cancha{courts.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Club Modal
const EditClubModal = ({ isOpen, onClose, club, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    state: '',
    country: 'México',
    address: '',
    phone: '',
    email: '',
    lat: '',
    lng: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (club) {
      setFormData({
        name: club.name || '',
        city: club.city || '',
        state: club.state || '',
        country: club.country || 'México',
        address: club.address || '',
        phone: club.phone || '',
        email: club.email || '',
        lat: club.location?.lat?.toString() || '',
        lng: club.location?.lng?.toString() || ''
      });
    }
  }, [club]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.city) {
      toast.error('Nombre y ciudad son obligatorios');
      return;
    }

    try {
      setSaving(true);
      
      // Prepare update data - only include fields that changed
      const updateData = {};
      if (formData.name !== club.name) updateData.name = formData.name;
      if (formData.city !== club.city) updateData.city = formData.city;
      if (formData.state !== club.state) updateData.state = formData.state;
      if (formData.country !== club.country) updateData.country = formData.country;
      if (formData.address !== club.address) updateData.address = formData.address;
      if (formData.phone !== club.phone) updateData.phone = formData.phone;
      if (formData.email !== club.email) updateData.email = formData.email;
      
      const currentLat = club.location?.lat?.toString() || '';
      const currentLng = club.location?.lng?.toString() || '';
      if (formData.lat && formData.lat !== currentLat) updateData.lat = parseFloat(formData.lat);
      if (formData.lng && formData.lng !== currentLng) updateData.lng = parseFloat(formData.lng);

      if (Object.keys(updateData).length === 0) {
        toast.info('No hay cambios para guardar');
        onClose();
        return;
      }

      await adminAPI.updateClub(club.id, updateData);
      toast.success('Club actualizado correctamente');
      onSave();
      onClose();
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.response?.data?.detail || 'Error actualizando club');
    } finally {
      setSaving(false);
    }
  };

  if (!club) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-lg max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-primary" />
            Editar Club
          </DialogTitle>
          <DialogDescription>
            Modifica los datos del club. Los campos con * son obligatorios.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="name">Nombre del Club *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nombre del club"
            />
          </div>
          
          {/* City & State */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="city">Ciudad *</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="Ciudad"
              />
            </div>
            <div>
              <Label htmlFor="state">Estado</Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="Estado/Provincia"
              />
            </div>
          </div>
          
          {/* Country */}
          <div>
            <Label htmlFor="country">País</Label>
            <Select 
              value={formData.country} 
              onValueChange={(val) => setFormData({ ...formData, country: val })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="México">🇲🇽 México</SelectItem>
                <SelectItem value="USA">🇺🇸 USA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Address */}
          <div>
            <Label htmlFor="address">Dirección</Label>
            <Input
              id="address"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Dirección completa"
            />
          </div>
          
          {/* Phone & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+52 XXX XXX XXXX"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@club.com"
              />
            </div>
          </div>
          
          {/* Coordinates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="lat">Latitud</Label>
              <Input
                id="lat"
                type="number"
                step="any"
                value={formData.lat}
                onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                placeholder="ej: 19.4326"
              />
            </div>
            <div>
              <Label htmlFor="lng">Longitud</Label>
              <Input
                id="lng"
                type="number"
                step="any"
                value={formData.lng}
                onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                placeholder="ej: -99.1332"
              />
            </div>
          </div>
          
          {/* Courts info (read-only) */}
          <div className="bg-secondary/50 rounded-lg p-3">
            <p className="text-sm text-muted-foreground">
              <strong>Canchas:</strong> {club.courts?.length || 0} canchas registradas
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Para agregar o quitar canchas, usa la función de gestión de canchas.
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// QR Code Preview Modal
const QRPreviewModal = ({ isOpen, onClose, qrData, clubName, courtNumber }) => {
  if (!qrData) return null;
  
  const handleDownload = () => {
    // Create download link from base64
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${qrData.base64}`;
    link.download = `${clubName.replace(/\s+/g, '_')}_cancha_${courtNumber}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR descargado');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-md max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <DialogHeader>
          <DialogTitle>QR Code - {clubName}</DialogTitle>
          <DialogDescription>Cancha {courtNumber}</DialogDescription>
        </DialogHeader>
        
        <div className="flex flex-col items-center p-4">
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <img 
              src={`data:image/png;base64,${qrData.base64}`} 
              alt={`QR Cancha ${courtNumber}`}
              className="w-64 h-64"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center break-all">
            {qrData.qr_data}
          </p>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button onClick={handleDownload} className="gap-2">
            <Download className="w-4 h-4" />
            Descargar PNG
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Club Card Component
const ClubCard = ({ club, onEdit, onDelete, onGenerateQR, onViewQRs, onManageCourts }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Safely get values with defaults
  const clubCountry = club.country || 'México';
  const clubCity = club.city || 'Ciudad';
  const clubState = club.state || clubCountry;
  const clubAddress = club.address || 'Sin dirección';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="glass rounded-xl p-4 space-y-3"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-primary" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold break-words">{club.name || 'Sin nombre'}</h3>
            <p className="text-sm text-muted-foreground">
              {clubCity}, {clubState}
            </p>
          </div>
        </div>
        
        {/* Badge with country flag */}
        <span className={`text-xs px-2 py-1 rounded-full ${
          clubCountry === 'USA' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
        }`}>
          {clubCountry === 'USA' ? '🇺🇸 USA' : '🇲🇽 México'}
        </span>
      </div>
      
      {/* Details */}
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <MapPin className="w-4 h-4 flex-shrink-0" />
          <span className="break-words">{clubAddress}</span>
        </div>
        {club.phone && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Phone className="w-4 h-4 flex-shrink-0" />
            <span>{club.phone}</span>
          </div>
        )}
        {club.email && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Mail className="w-4 h-4 flex-shrink-0" />
            <span className="break-all">{club.email}</span>
          </div>
        )}
      </div>
      
      {/* Courts - with manage button */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <span className="text-xs text-muted-foreground">Canchas:</span>
        {club.courts?.map((court) => (
          <button
            key={court.id}
            onClick={() => onGenerateQR(club, court)}
            className="w-8 h-8 shrink-0 rounded-lg bg-secondary flex items-center justify-center hover:bg-primary/20 transition-colors"
            title={`Generar QR Cancha ${court.court_number}`}
          >
            <span className="text-sm font-medium">{court.court_number}</span>
          </button>
        ))}
        <button
          onClick={() => onManageCourts(club)}
          className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
          title="Gestionar canchas"
        >
          <Settings className="w-4 h-4 text-primary" />
        </button>
      </div>
      
      {/* Actions - Stacked layout for mobile */}
      <div className="flex flex-col gap-2 pt-2 border-t border-border">
        {/* Ver QRs button */}
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-1"
          onClick={() => onViewQRs(club)}
        >
          <QrCode className="w-4 h-4" />
          Ver QRs
        </Button>
        
        {/* Edit & Delete buttons - always visible */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => onEdit(club)}
          >
            <Pencil className="w-4 h-4" />
            Editar
          </Button>
          
          {showDeleteConfirm ? (
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                className="flex-1"
                onClick={() => {
                  onDelete(club.id);
                  setShowDeleteConfirm(false);
                }}
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-destructive hover:text-destructive border-destructive/30 hover:border-destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4" />
              Borrar
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// All QRs Modal for a club
const AllQRsModal = ({ isOpen, onClose, club, qrCodes, loading }) => {
  const downloadAll = () => {
    qrCodes.forEach((qr, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = `data:image/png;base64,${qr.base64}`;
        link.download = `${club.name.replace(/\s+/g, '_')}_cancha_${qr.court_number}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 200); // Stagger downloads
    });
    toast.success(`${qrCodes.length} QRs descargados`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[88dvh] overflow-y-auto overscroll-contain pb-[calc(env(safe-area-inset-bottom)+1rem)]">
        <DialogHeader>
          <DialogTitle>Códigos QR - {club?.name}</DialogTitle>
          <DialogDescription>
            {qrCodes.length} canchas disponibles
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4">
            {qrCodes.map((qr) => (
              <div key={qr.court_id} className="flex flex-col items-center p-4 bg-secondary/50 rounded-xl">
                <div className="bg-white p-2 rounded-lg mb-2">
                  <img 
                    src={`data:image/png;base64,${qr.base64}`} 
                    alt={`QR Cancha ${qr.court_number}`}
                    className="w-32 h-32"
                  />
                </div>
                <p className="font-medium">Cancha {qr.court_number}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 gap-1"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = `data:image/png;base64,${qr.base64}`;
                    link.download = `${club.name.replace(/\s+/g, '_')}_cancha_${qr.court_number}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <Download className="w-3 h-3" />
                  Descargar
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button onClick={downloadAll} className="gap-2" disabled={loading || qrCodes.length === 0}>
            <Download className="w-4 h-4" />
            Descargar Todos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminClubs() {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  
  // QR Modal state
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [currentQR, setCurrentQR] = useState(null);
  const [currentClubName, setCurrentClubName] = useState('');
  const [currentCourtNumber, setCurrentCourtNumber] = useState(1);
  
  // All QRs Modal state
  const [allQRsModalOpen, setAllQRsModalOpen] = useState(false);
  const [allQRsClub, setAllQRsClub] = useState(null);
  const [allQRsCodes, setAllQRsCodes] = useState([]);
  const [loadingQRs, setLoadingQRs] = useState(false);
  
  // Edit Club Modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  
  // Add Club Modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  
  // Manage Courts Modal state
  const [courtsModalOpen, setCourtsModalOpen] = useState(false);
  const [managingClub, setManagingClub] = useState(null);

  const fetchClubs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getClubs();
      const clubsData = res.data.clubs || [];
      setClubs(clubsData);
    } catch (err) {
      console.error('Error fetching clubs:', err);
      
      // If unauthorized, redirect to login
      if (err.response?.status === 401) {
        toast.error('Sesión expirada. Por favor inicia sesión de nuevo.');
        navigate('/auth');
        return;
      }
      
      const errorMsg = err.response?.data?.detail || 'Error cargando clubs. Intenta recargar.';
      toast.error(errorMsg);
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  const handleGenerateQR = async (club, court) => {
    try {
      const res = await adminAPI.generateQR(club.id, court.id);
      setCurrentQR(res.data);
      setCurrentClubName(club.name);
      setCurrentCourtNumber(court.court_number);
      setQrModalOpen(true);
    } catch (err) {
      console.error('QR generation error:', err);
      toast.error('Error generando QR');
    }
  };

  const handleViewAllQRs = async (club) => {
    setAllQRsClub(club);
    setAllQRsModalOpen(true);
    setLoadingQRs(true);
    
    try {
      const res = await adminAPI.generateAllQRs(club.id);
      setAllQRsCodes(res.data.qr_codes || []);
    } catch (err) {
      console.error('Error loading QRs:', err);
      toast.error('Error cargando QRs');
    } finally {
      setLoadingQRs(false);
    }
  };

  // Open manage courts modal
  const handleManageCourts = (club) => {
    setManagingClub(club);
    setCourtsModalOpen(true);
  };

  const handleDeleteClub = async (clubId) => {
    try {
      await adminAPI.deleteClub(clubId);
      toast.success('Club eliminado');
      fetchClubs();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Error eliminando club');
    }
  };

  // Open edit modal with club data
  const handleEditClub = (club) => {
    setEditingClub(club);
    setEditModalOpen(true);
  };

  // Called when edit modal saves successfully
  const handleEditSave = () => {
    fetchClubs(); // Refresh the list
  };

  // Filter clubs
  const filteredClubs = clubs.filter(club => {
    const name = (club.name || '').toLowerCase();
    const city = (club.city || '').toLowerCase();
    const address = (club.address || '').toLowerCase();
    const searchLower = search.toLowerCase();
    
    const matchesSearch = 
      name.includes(searchLower) ||
      city.includes(searchLower) ||
      address.includes(searchLower);
    
    const clubCountry = club.country || 'México';
    const matchesCountry = 
      countryFilter === 'all' || 
      clubCountry === countryFilter;
    
    return matchesSearch && matchesCountry;
  });

  // Group by country
  const mexicoClubs = filteredClubs.filter(c => (c.country || 'México') !== 'USA');
  const usaClubs = filteredClubs.filter(c => c.country === 'USA');

  return (
    <div className="min-h-screen bg-background overflow-x-hidden admin-mobile">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Gestión de Clubs</h1>
              <p className="text-xs text-muted-foreground">{clubs.length} clubs registrados</p>
            </div>
          </div>
          
          <div className="w-full sm:w-auto flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchClubs}
              className="gap-1"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={() => setAddModalOpen(true)}
              className="gap-1"
              data-testid="admin-add-club-open-button"
            >
              <Plus className="w-4 h-4" />
              Agregar Club
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, ciudad..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant={countryFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCountryFilter('all')}
            >
              <Globe className="w-4 h-4 mr-1" />
              Todos
            </Button>
            <Button
              variant={countryFilter === 'México' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCountryFilter('México')}
            >
              🇲🇽 México
            </Button>
            <Button
              variant={countryFilter === 'USA' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCountryFilter('USA')}
            >
              🇺🇸 USA
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : clubs.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No hay clubs</h3>
            <p className="text-muted-foreground mb-4">
              No se encontraron clubs. Verifica tu conexión o intenta recargar.
            </p>
            <Button onClick={fetchClubs}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Recargar
            </Button>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Mexico Section */}
            {mexicoClubs.length > 0 && (
              <div>
                <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                  🇲🇽 México
                  <span className="text-sm font-normal text-muted-foreground">
                    ({mexicoClubs.length} clubs)
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <AnimatePresence>
                    {mexicoClubs.map((club) => (
                      <ClubCard
                        key={club.id}
                        club={club}
                        onEdit={handleEditClub}
                        onDelete={handleDeleteClub}
                        onGenerateQR={handleGenerateQR}
                        onViewQRs={handleViewAllQRs}
                        onManageCourts={handleManageCourts}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
            
            {/* USA Section */}
            {usaClubs.length > 0 && (
              <div>
                <h2 className="font-display font-bold text-lg mb-4 flex items-center gap-2">
                  🇺🇸 USA
                  <span className="text-sm font-normal text-muted-foreground">
                    ({usaClubs.length} clubs)
                  </span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <AnimatePresence>
                    {usaClubs.map((club) => (
                      <ClubCard
                        key={club.id}
                        club={club}
                        onEdit={handleEditClub}
                        onDelete={handleDeleteClub}
                        onGenerateQR={handleGenerateQR}
                        onViewQRs={handleViewAllQRs}
                        onManageCourts={handleManageCourts}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* QR Preview Modal */}
      <QRPreviewModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        qrData={currentQR}
        clubName={currentClubName}
        courtNumber={currentCourtNumber}
      />
      
      {/* All QRs Modal */}
      <AllQRsModal
        isOpen={allQRsModalOpen}
        onClose={() => {
          setAllQRsModalOpen(false);
          setAllQRsCodes([]);
        }}
        club={allQRsClub}
        qrCodes={allQRsCodes}
        loading={loadingQRs}
      />
      
      {/* Edit Club Modal */}
      <EditClubModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingClub(null);
        }}
        club={editingClub}
        onSave={handleEditSave}
      />
      
      {/* Add Club Modal */}
      <AddClubModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={fetchClubs}
      />
      
      {/* Manage Courts Modal */}
      <ManageCourtsModal
        isOpen={courtsModalOpen}
        onClose={() => {
          setCourtsModalOpen(false);
          setManagingClub(null);
        }}
        club={managingClub}
        onSave={fetchClubs}
      />
    </div>
  );
}
